import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link } from 'react-router'
import { Divider, Grid, Typography, useMediaQuery } from '@mui/material'

import ChannelBubbleGroup from '../../../components/channel-bubble/channel-bubble-group'
import PauseDuration from '../../../components/pause-duration'
import { AppContext, useHost } from '../../../providers/app-provider'
import Api from '../../../utils/api'
import { getFormattedDateTime, showDeviceMenuItems } from '../../../utils/helpers'
import useSharedStyles from '../../../utils/shared.styles'

import ActivationDetails from './activation-details'
import useStyles from './details.styles'
import DeviceField from './device-field'
import DevicePhoto from './device-photo'

const NETWORK_TYPE_LABELS: Record<string, string> = {
  lora: 'Sidewalk LoRa',
  fsk: 'Sidewalk FSK',
  ble: 'Sidewalk Bluetooth',
  lorawan: 'LoRaWAN',
  lte: 'LTE',
  relay: 'Meshify Relay',
}

const DeviceDetails = ({ device, setDevice }: { device: IDeviceAPIObject; setDevice: React.Dispatch<React.SetStateAction<IDeviceAPIObject | undefined>> }) => {
  const auth = useAuth()
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const host = useHost()
  const { tenantId, pageRefreshCounter } = useContext(AppContext)
  const [folderAttributes, setFolderAttributes] = useState<IFolderAttributesType | null>(null)

  const belowSmScreenMatches = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const belowMdScreenMatches = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const belowXlScreenMatches = useMediaQuery((theme) => theme.breakpoints.down('xl'))
  const fieldSize = belowMdScreenMatches ? 12 : belowXlScreenMatches ? 6 : 4

  const { lastCheckIn, updatedAt, uniqueId, nodeType, area, metadata, folderId, orderNodeId, orderNumber, orderTimeline } = device
  const { vanityName } = nodeType
  const { device_ownership: deviceOwnership } = metadata || {}

  const deviceBuilding = device.building
  const deviceFloor = device.floor
  const deviceLocation = area || metadata?.device_location || ''
  const deviceLocationNotes = metadata?.device_location_note || ''
  const gps: string | undefined = metadata?.device_location_gps || undefined
  // If a Defender Slim vanity ends with H######, it's a 2.0 Slim with LoRa capability, so we add (+LoRa)
  // For all other devices, show the node type vanity name
  const deviceType = nodeType.name === 'leak5s' && device.vanity?.match(/H\d{6}$/) ? `${vanityName} (+LoRa)` : vanityName

  const phyLayer = String(device.currentValues?.phy_layer?.value ?? '').toLowerCase()
  const networkType = NETWORK_TYPE_LABELS[phyLayer]

  const getFolderAttributes = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token) return
    const res = await Api.get(`${host}/api/v2/folders/${folderId}/attributes`)
    if (res.ok) {
      setFolderAttributes(res.body)
    }
  })
  useEffect(() => {
    if (folderId) {
      getFolderAttributes()
    }
  }, [folderId, host, pageRefreshCounter])

  // Sort order timeline by updatedAt and get the latest status change date
  const latestOrderStatus = orderTimeline?.sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())[0]

  let orderStatusText = 'N/A'
  if (latestOrderStatus) {
    orderStatusText = `${latestOrderStatus.message} ${getFormattedDateTime(latestOrderStatus.updatedAt, undefined, 'MM/dd/yyyy')}`
  }

  const { resourcesLink } = showDeviceMenuItems(device)

  return (
    <Grid container={true} sx={classes.container}>
      <Grid container={true} size={belowSmScreenMatches ? 12 : 'auto'} sx={classes.deviceOverview(belowSmScreenMatches)}>
        <Grid size={belowSmScreenMatches ? 12 : 'auto'} sx={classes.photoContainer(belowSmScreenMatches)}>
          <DevicePhoto device={device} setDevice={setDevice} />
        </Grid>

        <Grid container={true} spacing={3} size={belowSmScreenMatches ? 9 : 'grow'} sx={classes.deviceSummary(belowSmScreenMatches)}>
          <Grid size={12} sx={classes.locationDetails(belowSmScreenMatches)}>
            <Typography variant="body1" component="span">
              {deviceBuilding}
              {((deviceBuilding && deviceFloor) || (deviceBuilding && deviceLocation)) && ' • '}
            </Typography>

            {deviceFloor && (
              <Typography variant="body1" component="span">
                Floor {deviceFloor}
                {deviceFloor && deviceLocation && ' • '}
              </Typography>
            )}

            <Typography variant="body1" component="span">
              {deviceLocation}
            </Typography>

            <Typography variant="body1" component="div" sx={sharedClasses.secondaryText}>
              {deviceLocationNotes}
            </Typography>
          </Grid>

          <PauseDuration device={device} />

          <Grid size={12}>
            <ChannelBubbleGroup device={device} />
          </Grid>
        </Grid>
      </Grid>
      {!belowMdScreenMatches && (
        <Grid size="auto">
          <Divider orientation="vertical" variant="middle" />
        </Grid>
      )}
      <Grid container={true} spacing={2} size="grow" sx={classes.details}>
        <DeviceField xs={fieldSize} label="Device Model" value={deviceType} resourcesLink={resourcesLink} />
        <DeviceField xs={fieldSize} label="Last Check-in" value={lastCheckIn ? getFormattedDateTime(lastCheckIn) : undefined} />
        <DeviceField xs={fieldSize} label="Unique Id" value={uniqueId} />
        <DeviceField xs={fieldSize} label="Last Updated" value={updatedAt ? getFormattedDateTime(updatedAt) : undefined} />
        <DeviceField xs={fieldSize} label="Device Ownership" value={deviceOwnership as string} />
        <DeviceField xs={fieldSize} label="Activation Details" value={<ActivationDetails activationApp={folderAttributes?.activationApp} gps={gps} />} />
        {nodeType.name === 'leak5s' && <DeviceField xs={fieldSize} label="Network Type" value={networkType} />}
        {orderNodeId && orderNumber && (
          <DeviceField
            xs={fieldSize}
            label="Order #"
            value={
              <Link to={`/tenant/${tenantId}/orders/${orderNodeId}`}>
                {orderNumber}
                <br />
                {orderStatusText}
              </Link>
            }
          />
        )}
      </Grid>
    </Grid>
  )
}

export default DeviceDetails
